import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ticketService, { 
  TICKET_STATUSES, 
  TICKET_CATEGORIES,
  TICKET_STATUS_LABELS,
  TICKET_CATEGORY_LABELS
} from '../services/ticketService';
import { formatBeijingDateOnly } from '../utils/dateTimeBeijing';
import AlertModal from '../components/common/AlertModal';
import './AdminTicketList.css';

const AdminTicketList = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    page: 1,
    page_size: 5
  });
  const [totalCount, setTotalCount] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    category: 'technical',
    priority: 'medium',
    user_email: '',
    user_phone: '',
    source: 'phone' // phone, email, other
  });
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page: filters.page,
        page_size: filters.page_size,
        ...(filters.status && { status: filters.status }),
        ...(filters.category && { category: filters.category })
      };
      
      const response = await ticketService.getAllTickets(params);
      const ticketsList = response.tickets || [];
      
      // 调试：检查数据结构
      if (ticketsList.length > 0) {
      }
      
      setTickets(ticketsList);
      setTotalCount(response.total || 0);
      
    } catch (err) {
      setError(err.message || '加载工单失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.category, filters.page, filters.page_size]);

  const loadStats = useCallback(async () => {
    try {
      const statsData = await ticketService.getAdminTicketStats();
      setStats(statsData);
    } catch (err) {
    }
  }, []);

  useEffect(() => {
    loadTickets();
    loadStats();
  }, [loadTickets, loadStats]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // 重置页码
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  

  const formatDate = (dateString) => {
    return formatBeijingDateOnly(dateString);
  };
  
  // 创建工单
  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) {
      setAlertModal({
        isOpen: true,
        title: '输入错误',
        message: '请填写工单标题和描述',
        type: 'warning'
      });
      return;
    }
    
    try {
      await ticketService.createTicket({
        title: newTicket.title,
        description: newTicket.description,
        category: newTicket.category,
        priority: newTicket.priority
      });
      
      setAlertModal({
        isOpen: true,
        title: '创建成功',
        message: '工单创建成功！',
        type: 'success'
      });
      
      setShowCreateModal(false);
      setNewTicket({
        title: '',
        description: '',
        category: 'technical',
        priority: 'medium',
        user_email: '',
        user_phone: '',
        source: 'phone'
      });
      await loadTickets();
      await loadStats();
    } catch (error) {
      setAlertModal({
        isOpen: true,
        title: '创建失败',
        message: error.message || '创建工单失败，请稍后重试',
        type: 'error'
      });
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingText>加载中...</LoadingText>
        </LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>工单管理</PageTitle>
        <HeaderActions>
          <CreateTicketButton onClick={() => setShowCreateModal(true)}>
            创建工单
          </CreateTicketButton>
        </HeaderActions>
      </PageHeader>

      {/* 统计卡片 */}
      {stats && (
        <StatsSection>
          <StatCard>
            <StatValue>{stats.total_tickets}</StatValue>
            <StatLabel>总工单</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.pending_tickets}</StatValue>
            <StatLabel>待处理</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.in_progress_tickets}</StatValue>
            <StatLabel>处理中</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.resolved_tickets}</StatValue>
            <StatLabel>已解决</StatLabel>
          </StatCard>
        </StatsSection>
      )}

      {/* 筛选区域 */}
      <FiltersRow>
        <FilterSelect 
          value={filters.status} 
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          <option value="">全部状态</option>
          <option value={TICKET_STATUSES.PENDING}>待处理</option>
          <option value={TICKET_STATUSES.IN_PROGRESS}>处理中</option>
          <option value={TICKET_STATUSES.RESOLVED}>已解决</option>
        </FilterSelect>

        <FilterSelect 
          value={filters.category} 
          onChange={(e) => handleFilterChange('category', e.target.value)}
        >
          <option value="">全部分类</option>
          <option value={TICKET_CATEGORIES.TECHNICAL}>技术问题</option>
          <option value={TICKET_CATEGORIES.ACCOUNT}>账户问题</option>
          <option value={TICKET_CATEGORIES.PAYMENT}>支付问题</option>
          <option value={TICKET_CATEGORIES.FEATURE_REQUEST}>功能建议</option>
          <option value={TICKET_CATEGORIES.BUG_REPORT}>问题反馈</option>
          <option value={TICKET_CATEGORIES.OTHER}>其他</option>
        </FilterSelect>
      </FiltersRow>

      {/* 工单列表 */}
      {error ? (
        <ErrorMessage>{error}</ErrorMessage>
      ) : (
        <>
          {/* 记录信息 - 右上角 */}
          {totalCount > 0 && (
            <TableRecordInfoWrapper>
              <TableRecordInfo>
                共 {totalCount} 条记录（第 {filters.page} 页，每页 {filters.page_size} 条）
              </TableRecordInfo>
            </TableRecordInfoWrapper>
          )}
          <TicketTableContainer>
            <TicketTable>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell width="60px">序号</TableHeaderCell>
              <TableHeaderCell width="140px">工单编号</TableHeaderCell>
              <TableHeaderCell>标题</TableHeaderCell>
              <TableHeaderCell width="100px">分类</TableHeaderCell>
              <TableHeaderCell width="100px">状态</TableHeaderCell>
              <TableHeaderCell width="100px">用户</TableHeaderCell>
              <TableHeaderCell width="80px">回复数</TableHeaderCell>
              <TableHeaderCell width="120px">创建时间</TableHeaderCell>
              <TableHeaderCell width="220px">操作</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {tickets.map((ticket, index) => (
              <TableRow key={ticket.id}>
                <TableCell>
                  <IndexNumber>{(filters.page - 1) * filters.page_size + index + 1}</IndexNumber>
                </TableCell>
                <TableCell>
                  <TicketId>{ticket.ticket_id}</TicketId>
                </TableCell>
                <TableCell>
                  <TicketTitle 
                    onClick={() => {
                      if (!ticket.id) {
                        return;
                      }
                      navigate(`/sys-portal/tickets/${ticket.id}`);
                    }}
                  >
                    {ticket.title}
                  </TicketTitle>
                </TableCell>
                <TableCell>
                  <CategoryBadge category={ticket.category}>
                    {TICKET_CATEGORY_LABELS[ticket.category]}
                  </CategoryBadge>
                </TableCell>
                <TableCell>
                  <StatusBadge status={ticket.status}>
                    {TICKET_STATUS_LABELS[ticket.status]}
                  </StatusBadge>
                </TableCell>
                <TableCell>
                  <UserInfo>{ticket.user_nickname}</UserInfo>
                </TableCell>
                <TableCell>
                  <ReplyCount>{ticket.reply_count}</ReplyCount>
                </TableCell>
                <TableCell>
                  <TimeInfo>{formatDate(ticket.created_at)}</TimeInfo>
                </TableCell>
                <TableCell>
                  <ActionButtons>
                    <ActionButton 
                      className="btn-info"
                      onClick={() => {
                        if (!ticket.id) {
                          return;
                        }
                        navigate(`/sys-portal/tickets/${ticket.id}`);
                      }}
                    >
                      查看
                    </ActionButton>
                  </ActionButtons>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
            </TicketTable>
          </TicketTableContainer>
        </>
      )}

      {/* 分页按钮 - 底部居中 */}
      {totalCount > filters.page_size && (
        <PaginationButtonsContainer>
          <PaginationButton 
            onClick={() => handlePageChange(1)}
            disabled={filters.page === 1}
          >
            首页
          </PaginationButton>
          <PaginationButton 
            onClick={() => handlePageChange(filters.page - 1)}
            disabled={filters.page === 1}
          >
            上一页
          </PaginationButton>
          <PageIndicator>
            {filters.page} / {Math.ceil(totalCount / filters.page_size)}
          </PageIndicator>
          <PaginationButton 
            onClick={() => handlePageChange(filters.page + 1)}
            disabled={filters.page >= Math.ceil(totalCount / filters.page_size)}
          >
            下一页
          </PaginationButton>
          <PaginationButton 
            onClick={() => handlePageChange(Math.ceil(totalCount / filters.page_size))}
            disabled={filters.page >= Math.ceil(totalCount / filters.page_size)}
          >
            末页
          </PaginationButton>
        </PaginationButtonsContainer>
      )}
      
      {/* 创建工单弹窗 */}
      {showCreateModal && (
        <ModalOverlay onClick={() => setShowCreateModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <ModalHeader>
              <ModalTitle>创建工单（管理员）</ModalTitle>
              <CloseButton onClick={() => setShowCreateModal(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <CreateTicketNote>
                此功能用于管理员手动创建工单，例如通过电话、邮件或其他渠道收到的反馈问题。
              </CreateTicketNote>
              
              <FormGroup>
                <FormLabel>工单来源 <RequiredMark>*</RequiredMark></FormLabel>
                <FormSelect 
                  value={newTicket.source}
                  onChange={(e) => setNewTicket({...newTicket, source: e.target.value})}
                >
                  <option value="phone">电话反馈</option>
                  <option value="email">邮件反馈</option>
                  <option value="other">其他渠道</option>
                </FormSelect>
              </FormGroup>
              
              <FormRow>
                <FormGroup>
                  <FormLabel>用户邮箱</FormLabel>
                  <FormInput 
                    type="email"
                    placeholder="请输入用户邮箱（选填）"
                    value={newTicket.user_email}
                    onChange={(e) => setNewTicket({...newTicket, user_email: e.target.value})}
                  />
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>用户电话</FormLabel>
                  <FormInput 
                    type="tel"
                    placeholder="请输入用户电话（选填）"
                    value={newTicket.user_phone}
                    onChange={(e) => setNewTicket({...newTicket, user_phone: e.target.value})}
                  />
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <FormLabel>工单标题 <RequiredMark>*</RequiredMark></FormLabel>
                <FormInput 
                  type="text"
                  placeholder="请输入工单标题"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                />
              </FormGroup>
              
              <FormRow>
                <FormGroup>
                  <FormLabel>问题分类 <RequiredMark>*</RequiredMark></FormLabel>
                  <FormSelect 
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                  >
                    <option value="technical">技术问题</option>
                    <option value="account">账户问题</option>
                    <option value="payment">支付问题</option>
                    <option value="feature_request">功能建议</option>
                    <option value="bug_report">问题反馈</option>
                    <option value="other">其他</option>
                  </FormSelect>
                </FormGroup>
                
                <FormGroup>
                  <FormLabel>优先级 <RequiredMark>*</RequiredMark></FormLabel>
                  <FormSelect 
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                  >
                    <option value="low">低</option>
                    <option value="medium">中</option>
                    <option value="high">高</option>
                    <option value="urgent">紧急</option>
                  </FormSelect>
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <FormLabel>问题描述 <RequiredMark>*</RequiredMark></FormLabel>
                <FormTextArea 
                  placeholder="请详细描述问题内容..."
                  rows="6"
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                />
              </FormGroup>
            </ModalBody>
            
            <ModalFooter>
              <CancelButton onClick={() => setShowCreateModal(false)}>
                取消
              </CancelButton>
              <SubmitButton 
                onClick={handleCreateTicket}
                disabled={!newTicket.title.trim() || !newTicket.description.trim()}
              >
                创建工单
              </SubmitButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* 统一错误提示弹框 */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </PageContainer>
  );
};

export default AdminTicketList;

// 样式组件
const PageContainer = styled.div`
  padding: 24px;
  background: #f8fafc;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const CreateTicketButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  border: 1px solid ${props => props.highlight ? '#f56565' : '#e2e8f0'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.highlight ? '#f56565' : '#2d3748'};
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #718096;
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

const FilterSelect = styled.select`
  width: 160px;
  padding: 10px 36px 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: #2d3748;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234a5568' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  
  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TableRecordInfoWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const TableRecordInfo = styled.div`
  font-size: 14px;
  color: #718096;
`;

const TicketTableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TicketTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f7fafc;
`;

const TableHeaderRow = styled.tr`
  background: #f7fafc;
  border-bottom: 2px solid #cbd5e0;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e2e8f0;
  background: white;
  transition: background-color 0.2s ease;
  
  &:nth-child(even) {
    background-color: #fcfdfe;
  }
  
  &:hover {
    background-color: #f7fafc;
  }
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: #f7fafc;
  width: ${props => props.width || 'auto'};
`;

const TableBody = styled.tbody``;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #2d3748;
  vertical-align: middle;
`;

const IndexNumber = styled.div`
  text-align: center;
  font-weight: 600;
  color: #667eea;
  font-size: 14px;
`;

const TicketId = styled.div`
  font-family: monospace;
  font-size: 12px;
  color: #667eea;
  font-weight: 500;
`;

const TicketTitle = styled.div`
  font-weight: 500;
  color: #2d3748;
  cursor: pointer;
  
  &:hover {
    color: #667eea;
  }
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 4px 0;
  font-size: 14px;
  font-weight: 500;
  color: #2d3748;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  background: ${props => {
    switch(props.status) {
      case 'pending': return '#fed7d7';
      case 'in_progress': return '#bee3f8';
      case 'resolved': return '#c6f6d5';
      default: return '#e2e8f0';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'pending': return '#c53030';
      case 'in_progress': return '#2c5282';
      case 'resolved': return '#2f855a';
      default: return '#4a5568';
    }
  }};
`;

const UserInfo = styled.div`
  font-weight: 500;
`;


const ReplyCount = styled.div`
  text-align: center;
  font-weight: 500;
`;

const TimeInfo = styled.div`
  font-size: 13px;
  color: #718096;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 4px;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
`;

const ActionButton = styled.button`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &.btn-info {
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
  }
  
  &.btn-info:hover {
    background: linear-gradient(135deg, #5568d3, #6a3f8f);
  }
  
  &.btn-warning {
    background: #ed8936;
    color: white;
  }
  
  &.btn-warning:hover {
    background: #dd6b20;
  }
  
  &.btn-danger {
    background: #f56565;
    color: white;
  }
  
  &.btn-danger:hover {
    background: #e53e3e;
  }
  
  &.btn-success {
    background: #48bb78;
    color: white;
  }
  
  &.btn-success:hover {
    background: #38a169;
  }
  
  &.btn-primary {
    background: #4299e1;
    color: white;
  }
  
  &.btn-primary:hover {
    background: #3182ce;
  }
  
  &.btn-secondary {
    background: #718096;
    color: white;
  }
  
  &.btn-secondary:hover {
    background: #4a5568;
  }
`;

const PaginationButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
`;

const PaginationButton = styled.button`
  padding: 6px 14px;
  background: #e2e8f0;
  color: #4a5568;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #cbd5e0;
    color: #2d3748;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageIndicator = styled.span`
  padding: 6px 12px;
  font-size: 13px;
  color: #2d3748;
  background: white;
  border-radius: 4px;
  font-weight: 500;
  border: 1px solid #e2e8f0;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const LoadingText = styled.div`
  color: #718096;
  font-size: 14px;
`;

const ErrorMessage = styled.div`
  background: #fff5f5;
  color: #c53030;
  padding: 16px 20px;
  border-radius: 8px;
  border: 1px solid #feb2b2;
  font-size: 14px;
  margin-bottom: 20px;
`;

// 创建工单弹窗样式
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #f1f5f9;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
    color: #334155;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #f1f5f9;
`;

const CreateTicketNote = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #0c4a6e;
  line-height: 1.5;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background: white;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const FormTextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  background: white;
  resize: vertical;
  min-height: 90px;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: #64748b;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    color: #374151;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const RequiredMark = styled.span`
  color: #ef4444;
  font-weight: 600;
`;
